/**
 * "What Our Customers Say" carousel (sections/reviews-carousel.liquid).
 *
 * 1. Carousel behaviour — scroll-edge fades and pagination dots. Moved here
 *    from the section's old inline script so it can be (re)applied after
 *    the slides change.
 * 2. Service reviews — loads this store's latest APPROVED service reviews
 *    (customers rating the overall buying experience, collected via the
 *    service-review email/page) from the review portal's public endpoint:
 *      GET /service-reviews/public?page=1&page_size=&sort=newest  (X-Store-Key required)
 *    and renders them with the same card markup, replacing the manual
 *    reviews (section blocks). With none approved yet — or the backend
 *    unreachable — the manual reviews stay, or, with the section's
 *    "show manual reviews" setting off, the section stays hidden.
 *
 * Review content is customer-submitted, so everything from the API is built
 * with DOM APIs (createElement/textContent/setAttribute) — never innerHTML.
 */

const STAR_PATH =
  'M9 1.5L11.3175 6.195L16.5 6.9675L12.75 10.62L13.635 15.78L9 13.3425L4.365 15.78L5.25 10.62L1.5 6.9675L6.6825 6.195L9 1.5Z';
const ACCENT = '#E8733A';
const SVG_NS = 'http://www.w3.org/2000/svg';

/** @param {string} tag @param {Record<string, string>} [attrs] @param {(Node|string)[]} [children] */
function h(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value == null) continue;
    if (key === 'class') node.className = value;
    else node.setAttribute(key, value);
  }
  for (const child of children) {
    if (child == null) continue;
    node.append(child);
  }
  return node;
}

function svg(attrs, children) {
  const node = document.createElementNS(SVG_NS, attrs.tag);
  for (const [key, value] of Object.entries(attrs)) if (key !== 'tag') node.setAttribute(key, value);
  node.append(...children);
  return node;
}

/** One star, identical to the Liquid ones (filled or outline). */
function buildStar(filled) {
  const path = svg(
    {
      tag: 'path',
      d: STAR_PATH,
      fill: filled ? ACCENT : 'none',
      stroke: ACCENT,
      'stroke-width': '1.5',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    },
    [],
  );
  return svg({ tag: 'svg', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', 'aria-hidden': 'true' }, [path]);
}

/** The "Verified Buyer" tick, identical to the Liquid one. */
function buildVerifiedIcon() {
  return svg({ tag: 'svg', width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none', 'aria-hidden': 'true' }, [
    svg({ tag: 'circle', cx: '8', cy: '8', r: '8', fill: ACCENT }, []),
    svg(
      { tag: 'path', d: 'M4.5 8L7 10.5L11.5 5.5', stroke: '#fff', 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
      [],
    ),
  ]);
}

function buildSlide(review, index, colCount) {
  const stars = h('div', { class: 'rc-card__stars', role: 'img', 'aria-label': `${review.rating} out of 5 stars` }, [
    ...[1, 2, 3, 4, 5].map((i) => buildStar(i <= review.rating)),
  ]);
  const footer = h('div', { class: 'rc-card__footer' }, [
    h('span', { class: 'rc-card__name' }, [review.display_name]),
    review.verified_purchase ? h('span', { class: 'rc-card__verified' }, [buildVerifiedIcon(), ' Verified Buyer']) : null,
  ]);
  return h('div', { class: `rc-slide${index % colCount === 0 ? ' rc-slide--snap' : ''}` }, [
    h('div', { class: 'rc-card' }, [
      stars,
      review.review_title ? h('p', { class: 'rc-card__title' }, [review.review_title]) : null,
      review.review_text ? h('p', { class: 'rc-card__body rc-card__body--plain' }, [review.review_text]) : null,
      footer,
    ]),
  ]);
}

/**
 * Edge fades + pagination dots for the current slides. Safe to call again
 * after the slides are replaced: the previous listeners are removed first
 * and the dots are rebuilt for the new slide count.
 */
function wireCarousel(section) {
  section.carouselAbort?.abort();
  const abort = new AbortController();
  section.carouselAbort = abort;
  const { signal } = abort;

  const track = section.querySelector('.rc-track');
  if (!track) return;
  const carousel = track.parentElement;
  const slides = track.querySelectorAll('.rc-slide');
  const colCount = Number(section.dataset.colCount) || 2;
  const effectiveColCount = () => (window.innerWidth <= 749 ? 1 : colCount);
  const slideWidth = () => slides[0].offsetWidth + (parseFloat(getComputedStyle(track).gap) || 0);

  // Dots: one per page of colCount slides, only when there's more than one page.
  carousel.querySelector('.rc-dots')?.remove();
  const pageCount = Math.ceil(slides.length / colCount);
  let dots = [];
  if (section.dataset.showDots === 'true' && pageCount > 1) {
    dots = Array.from({ length: pageCount }, (_, i) =>
      h('button', { type: 'button', class: `rc-dot${i === 0 ? ' rc-dot--active' : ''}`, 'aria-label': `Go to page ${i + 1}` }),
    );
    carousel.append(h('div', { class: 'rc-dots' }, dots));
    dots.forEach((dot, i) =>
      dot.addEventListener(
        'click',
        () => {
          const scrollPad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
          const target = i === 0 ? 0 : i * effectiveColCount() * slideWidth() - scrollPad;
          track.scrollTo({ left: target, behavior: 'smooth' });
        },
        { signal },
      ),
    );
  }
  if (!slides.length) return;

  function updateDots() {
    const idx = Math.round(track.scrollLeft / (effectiveColCount() * slideWidth()));
    dots.forEach((d, i) => d.classList.toggle('rc-dot--active', i === idx));
  }

  function updateFade() {
    const threshold = 10;
    const hasPrev = track.scrollLeft > threshold;
    const hasNext = track.scrollLeft < track.scrollWidth - track.clientWidth - threshold;
    carousel.classList.toggle('rc-carousel--has-prev', hasPrev);
    carousel.classList.toggle('rc-carousel--has-next', hasNext);

    const trackRect = track.getBoundingClientRect();
    if (hasPrev) {
      const first = Array.from(slides).find((s) => s.getBoundingClientRect().left - trackRect.left >= 0);
      carousel.style.setProperty('--rc-left-stop', `${first ? first.getBoundingClientRect().left - trackRect.left : 0}px`);
    }
    if (hasNext) {
      const last = Array.from(slides).reverse().find((s) => trackRect.right - s.getBoundingClientRect().right >= 0);
      carousel.style.setProperty('--rc-right-stop', `${last ? trackRect.right - last.getBoundingClientRect().right : 0}px`);
    }
  }

  track.addEventListener(
    'scroll',
    () => {
      updateDots();
      updateFade();
    },
    { passive: true, signal },
  );
  updateFade();
}

async function loadServiceReviews(section) {
  const { apiBase = '', storeKey, pageSize = '10' } = section.dataset;
  // The key scopes the feed to THIS store — the endpoint rejects a request
  // without one, so never call it with a blank setting.
  if (!storeKey) {
    console.error('reviews-carousel: no store key configured (section setting "Reviews store key").');
    return;
  }

  let reviews = [];
  try {
    const url = new URL(`${apiBase.replace(/\/$/, '')}/service-reviews/public`);
    url.searchParams.set('page', '1');
    url.searchParams.set('page_size', pageSize);
    url.searchParams.set('sort', 'newest');
    const response = await fetch(url.toString(), { headers: { 'X-Store-Key': storeKey } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    reviews = (await response.json()).items || [];
  } catch (error) {
    console.error('reviews-carousel: could not load service reviews —', error);
  }

  // None approved yet (or backend unreachable): keep what the section
  // rendered — the manual reviews, or nothing (section stays hidden).
  if (!reviews.length) return;

  const colCount = Number(section.dataset.colCount) || 2;
  const track = section.querySelector('.rc-track');
  track.replaceChildren(...reviews.map((review, i) => buildSlide(review, i, colCount)));
  track.scrollLeft = 0;
  section.hidden = false;
  wireCarousel(section);
}

document.querySelectorAll('.rc-section[data-reviews-carousel]').forEach((section) => {
  wireCarousel(section);
  loadServiceReviews(section);
});
